// J1 join with a phone number, or on a neighbour's vouch.

import { html } from '../html.ts';
import { items, lang, t } from '../i18n.ts';
import { go, local, on, onForm, refuse } from '../core.ts';
import type { Screen } from '../core.ts';
import { world as w } from '../world.ts';
import { codeOf, isMember, param, vouchesFor } from '../queries.ts';
import { back, linkButton } from '../ui.ts';

export function welcomeScreen(): Screen {
  const me = w.me;
  return {
    title: t('app.name'),
    tab: null,
    body: html`
      <h1 tabindex="-1" class="display">${t('app.name')}</h1>
      <p class="lead strong">${t('join.title')}${lang() === 'hi' ? '।' : '.'}</p>
      <p>${t('join.body')}</p>
      <form class="stack" data-form="start">
        <label class="field"><span>${t('join.name')}</span>
          <input name="name" required maxlength="40" autocomplete="name" value="${me.name.en}" /></label>
        <button class="btn" type="submit" name="via" value="phone">${t('join.withPhone')}</button>
        <button class="quiet" type="submit" name="via" value="vouch">${t('join.withVouch')}</button>
      </form>
    `,
  };
}

onForm('start', (fd, form) => {
  if (!form.reportValidity()) return;
  const name = String(fd.get('name')).trim();
  const p = w.me;
  if (name && name !== p.name.en) {
    p.name = { en: name, hi: name };
    w.persist();
  }
  const via = String(fd.get('via') ?? 'phone');
  go(via === 'vouch' ? '#/join/vouch' : '#/join/phone');
});

export function phoneScreen(): Screen {
  const l = local();
  return {
    title: t('phone.title'),
    tab: null,
    body: html`
      ${back('#/')}
      <form class="stack" data-form="phone">
        <label class="field">
          <span><h1 tabindex="-1" class="as-label">${t('phone.title')}</h1></span>
          <span class="tel"><span aria-hidden="true">+91</span>
          <input name="phone" type="tel" inputmode="numeric" autocomplete="tel-national" required pattern="[6-9][0-9]{9}" maxlength="10" placeholder="98765 43210" /></span>
        </label>
        <p>${t('phone.help')}</p>
        ${l.error ? html`<p class="error" role="alert">${l.error}</p>` : ''}
        <button class="btn" type="submit">${t('phone.send')}</button>
      </form>
    `,
  };
}

onForm('phone', async (fd, form) => {
  const l = local();
  const digits = String(fd.get('phone')).replace(/\D/g, '');
  (form.elements.namedItem('phone') as HTMLInputElement).value = digits;
  if (!form.reportValidity()) return;
  const fingerprint = await w.phoneFingerprint(digits);
  if (w.phones.includes(fingerprint)) {
    l.error = t('phone.taken');
    return go('#/join/phone');
  }
  l.error = undefined;
  const code = String(100000 + (crypto.getRandomValues(new Uint32Array(1))[0]! % 900000));
  l.otp = { code, last: digits.slice(-2), fingerprint };
  go('#/join/code');
});

export function codeScreen(): Screen {
  const l = local();
  if (!l.otp) return phoneScreen();
  return {
    title: t('code.title'),
    tab: null,
    body: html`
      ${back('#/join/phone')}
      <form class="stack" data-form="code">
        <label class="field">
          <span><h1 tabindex="-1" class="as-label">${t('code.title')}</h1></span>
          <input name="code" class="code-input" inputmode="numeric" autocomplete="one-time-code" required pattern="[0-9]{6}" maxlength="6" />
        </label>
        <p>${t('code.sent', { last: l.otp.last })}</p>
        <p class="sandbox" data-demo-code="${l.otp.code}">${t('code.demo', { code: l.otp.code })}</p>
        ${l.error ? html`<p class="error" role="alert">${l.error}</p>` : ''}
        <button class="btn" type="submit">${t('code.check')}</button>
      </form>
    `,
  };
}

onForm('code', async (fd, form) => {
  if (!form.reportValidity()) return;
  const l = local();
  if (!l.otp || String(fd.get('code')).trim() !== l.otp.code) {
    l.error = t('code.wrong');
    return go('#/join/code');
  }
  // The issuer signs that this key's number was checked. The number itself goes nowhere.
  w.phones.push(l.otp.fingerprint);
  const { d } = await w.act('phone', { predicate: 'verified.phone', subject: w.me.id });
  l.otp = undefined;
  l.error = undefined;
  l.verified = d.admitted;
  go('#/join/ready');
});

export function readyScreen(): Screen {
  return {
    title: t('verified.title'),
    tab: null,
    body: html`
      <h1 tabindex="-1">${t('verified.title')}</h1>
      <p class="lead">${t('verified.body')}</p>
      <button class="btn" type="button" data-act="join">${t('join.go')}</button>
    `,
  };
}

export function vouchWaitScreen(): Screen {
  const me = w.me;
  const v = vouchesFor(w, me.id)[0];
  return {
    title: t('vouchAsk.title'),
    tab: null,
    body: html`
      ${back('#/')}
      <h1 tabindex="-1">${t('vouchAsk.title')}</h1>
      <p class="lead">${t('vouchAsk.body')}</p>
      <p class="code-big" data-testid="vouch-code" aria-label="${codeOf(me.id).split('').join(' ')}">${codeOf(me.id)}</p>
      ${v
        ? html`<p class="note">${t('vouchAsk.done', { name: w.name(v.author) })}</p>
            <button class="btn" type="button" data-act="join">${t('join.go')}</button>`
        : html`<p class="muted" role="status">${t('vouchAsk.waiting')}</p>`}
    `,
  };
}

on('join', async () => {
  const { d } = await w.act(w.seat, { predicate: 'join', subject: w.p('community').id });
  if (!d.admitted) {
    refuse('join', t('why.join'), d);
    return go('#/');
  }
  go('#/joined');
});

export function joinedScreen(): Screen {
  if (!isMember(w, w.me.id)) return welcomeScreen();
  return {
    title: t('joined.title'),
    tab: null,
    body: html`
      <h1 tabindex="-1">${t('joined.title')}</h1>
      <p class="lead">${t('joined.body', { items: items(param(w, 'newcomerOfferLimit')), limit: param(w, 'offerLimit'), days: Math.round(param(w, 'rampAfter') / 86_400_000) })}</p>
      ${linkButton(t('joined.cta'), '#/')}
    `,
  };
}
